import type { CollapsedSample } from '../data/collapseCoincident';
import { toColumnId, toSampleId } from '../data/markIds';
import { toColumnSample, type BinnedColumn } from '../geometry/binnedColumns';
import type { MarkListItem } from '../interaction/markNavigation';
import type { SurfaceMarks } from '../controller/surfaceChartController';
import type { XyzDataPoint } from '../types';

const LABEL_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const compareIds = (left: string, right: string): number => {
  if (left === right) return 0;
  return left < right ? -1 : 1;
};

const byLabelThenId = (left: MarkListItem, right: MarkListItem): number =>
  LABEL_COLLATOR.compare(left.label, right.label) ||
  compareIds(left.id, right.id);

export type AxisTitles = {
  xTitle: string;
  yTitle: string;
  zTitle: string;
  format: (value: number) => string;
};

export type PointListArgs = AxisTitles & {
  points: readonly XyzDataPoint[];
  /** The size field's title on the bubble chart, or undefined where mark size encodes nothing. */
  radiusTitle: string | undefined;
};

const toPositionDetails = (
  { x, y, z }: { x: number; y: number; z: number },
  { xTitle, yTitle, zTitle, format }: AxisTitles
): string[] => [
  `${xTitle}: ${format(x)}`,
  `${yTitle}: ${format(y)}`,
  `${zTitle}: ${format(z)}`,
];

const toSizeDetails = (
  radius: number | null,
  radiusTitle: string | undefined,
  format: (value: number) => string
): string[] => {
  if (!radiusTitle || radius === null) return [];
  return [`${radiusTitle}: ${format(radius)}`];
};

/** One row per plotted component, by name, with the values the tooltip shows. */
export const toPointListItems = ({
  points,
  radiusTitle,
  ...titles
}: PointListArgs): MarkListItem[] =>
  points
    .map(point => ({
      id: point.uniqueId,
      label: point.label,
      details: [
        ...toPositionDetails(point, titles),
        ...toSizeDetails(point.radius, radiusTitle, titles.format),
      ],
    }))
    .sort(byLabelThenId);

export type SampleListArgs = AxisTitles & {
  samples: readonly CollapsedSample[];
};

/** One row per surface sample position, named by the components that landed on it. */
export const toSampleListItems = ({
  samples,
  ...titles
}: SampleListArgs): MarkListItem[] =>
  samples
    .map(sample => ({
      id: toSampleId(sample),
      label: sample.points.map(({ label }) => label).join(', '),
      details: toPositionDetails(sample, titles),
    }))
    .sort(byLabelThenId);

export type ColumnListArgs = AxisTitles & {
  columns: readonly BinnedColumn[];
};

const toRangeDetail = (
  title: string,
  [low, high]: readonly [number, number],
  format: (value: number) => string
): string => `${title}: ${format(low)} – ${format(high)}`;

const toComponentCount = (count: number): string =>
  count === 1 ? '1 component' : `${count} components`;

/** One row per occupied bin: its range on both axes, the aggregate, who is in it and how many. */
export const toColumnListItems = ({
  columns,
  xTitle,
  yTitle,
  zTitle,
  format,
}: ColumnListArgs): MarkListItem[] =>
  columns
    .map(column => ({
      id: toColumnId(column),
      label: column.points.map(({ label }) => label).join(', '),
      details: [
        toRangeDetail(xTitle, column.xBounds, format),
        toRangeDetail(yTitle, column.yBounds, format),
        `${zTitle}: ${format(column.value)}`,
        toComponentCount(column.points.length),
      ],
    }))
    .sort(byLabelThenId);

export type SurfaceListArgs = AxisTitles & { marks: SurfaceMarks };

export const toSurfaceListItems = ({
  marks,
  ...titles
}: SurfaceListArgs): MarkListItem[] =>
  marks.kind === 'columns'
    ? toColumnListItems({ columns: marks.columns, ...titles })
    : toSampleListItems({ samples: marks.samples, ...titles });

/** The sample a host's click callback receives for a listed row. */
export const toListedSample = (
  marks: SurfaceMarks,
  id: string
): CollapsedSample | null => {
  if (marks.kind === 'samples') {
    return marks.samples.find(sample => toSampleId(sample) === id) ?? null;
  }
  const column = marks.columns.find(candidate => toColumnId(candidate) === id);
  return column ? toColumnSample(column) : null;
};

export type PointHighlightArgs = {
  hoveredId: string | null;
  listedId: string | null;
  isTurning: boolean;
  contextIds: readonly string[] | null;
};

export type PointHighlights = {
  highlightedIds: Set<string> | null;
  emphasisedIds: Set<string> | null;
};

const toSetOrNull = (ids: readonly string[]): Set<string> | null =>
  ids.length > 0 ? new Set(ids) : null;

/**
 * The marks the renderer lights and rings. A row active in the mark list is lit like a hover and
 * also ringed, so it reads apart from the pointer's, and it stays lit while the plot turns.
 */
export const toPointHighlights = ({
  hoveredId,
  listedId,
  isTurning,
  contextIds,
}: PointHighlightArgs): PointHighlights => {
  const hovered = hoveredId !== null && !isTurning ? [hoveredId] : [];
  const listed = listedId === null ? [] : [listedId];
  return {
    highlightedIds: toSetOrNull([...hovered, ...listed]),
    emphasisedIds: toSetOrNull([...(contextIds ?? []), ...listed]),
  };
};
